import type { GetServerSideProps } from "next";

import {
  getHeaderInfoForCurrentSection,
  getCurrentSection,
  getDomainData,
  getCurrentNextIcon,
  default as RenderComponent,
} from "@/pages/c/[locale]/[[...paths]]";
import { getServerSideProps as getServerSidePropsForProperties } from "@/pages/c/[locale]/properties/[[...paths]]";

import { ServerSidePageLogic } from "@/backend/logic";
import { CacheByKeyService } from "@/backend/service/cache";
import { ProfileService } from "@/backend/service/profile";

import { DEFAULT_ROUTE, ENV } from "@/src/const";
import { StringHelper, URLHelper, UserAgentHelper } from "@/src/util";

/**
 * this was created to handle the legacy paths of the website
 * @param paths - the paths from the request
 * @returns the slug to use for the page
 */
function handlePaths(paths: string | string[] | undefined, locale: string) {
  //if no paths, return the default route
  if (!Array.isArray(paths) || paths.length === 0) {
    return {
      slug: DEFAULT_ROUTE ?? "home",
      isProperties: false,
      locale: locale,
    };
  }

  //if the first path is a locale, return the rest of the paths
  const [possibleLocale] = paths;
  if ((possibleLocale ?? "").length === 2) {
    const newPaths = paths.slice(1);

    //check for the properties pages
    if (newPaths?.[0] === "properties") {
      return {
        locale: possibleLocale,
        slug: newPaths.slice(1).join("/"),
        isProperties: true,
      };
    }

    //determine if there is a slug for another page
    return {
      locale: possibleLocale,
      slug: newPaths.length === 0 ? (DEFAULT_ROUTE ?? "home") : newPaths.join("/"),
      isProperties: false,
    };
  }

  // return the path as the slug
  return {
    locale: locale,
    slug: paths.join("/"),
    isProperties: false,
  };
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  if (ENV !== "prod") {
    return {
      redirect: {
        permanent: false,
        destination: `/c/en/${DEFAULT_ROUTE}`,
      },
    };
  }

  context.res.setHeader("Cache-Control", "public, s-maxage=10, stale-while-revalidate=59");

  try {
    //get the params and set variables
    const { hostname, byPassCache, isPreview, returnHeaders, trackingOff, locale: _locale, isToolkit } = ServerSidePageLogic.getAllParams(context);
    const paths = context.params?.paths;
    const { slug, isProperties, locale } = handlePaths(paths, _locale);

    //if is properties, handle using the properties serverSideProps
    if (isProperties) {
      return getServerSidePropsForProperties(context);
    }

    //get the data for the page
    const cacheResponse = await CacheByKeyService.cacheAllRequests(slug, hostname, locale, byPassCache);
    if (!cacheResponse?.page || !cacheResponse?.webConfig) {
      return {
        redirect: {
          permanent: false,
          destination: "/missing",
        },
      };
    }

    //transform the data for the page
    const webConfig = cacheResponse.webConfig;
    const page = cacheResponse.page;
    const domainData = getDomainData(hostname, page);
    const tracking = { ...domainData?.tracking, ...page?.property?.data?.attributes?.tracking };
    const searchParams = context.query ?? {};
    const profileSlug: string | null = StringHelper.checkIsStringOrIsNull(context.query?.["profile"], "");
    const profileResponse = profileSlug ? await ProfileService.getBySlug(profileSlug) : null;
    const currentSection = getCurrentSection(searchParams, page);
    const headerInfo = getHeaderInfoForCurrentSection(page, currentSection) ?? {
      header: null,
      backgroundColor: null,
    };

    if (page) {
      return {
        props: {
          page,
          marketingSources: cacheResponse.marketing ?? null,
          webConfig: webConfig ?? null,
          locale: isPreview ? "en" : locale,
          layout: page.layout,
          query: URLHelper.serializeParseUrlQuery(context.query),
          refreshId: page.slug,
          searchParams,
          ...headerInfo,
          currentSection,
          profile: profileResponse?.[0] ?? null,
          noAuthRequired: true,
          trackingEnabled: !trackingOff,
          mobileHint: UserAgentHelper.isMobile(context.req?.headers?.["user-agent"]),
          deviceWidthHint: UserAgentHelper.deviceWidthHint(context.req?.headers?.["user-agent"]),
          appleOrMobileHint: UserAgentHelper.appleOrMobile(context.req?.headers?.["user-agent"]),
          domain: hostname,
          headers: returnHeaders ? (context.req?.headers ?? {}) : {},
          nav: cacheResponse.nav ?? null,
          tracking: tracking ?? null,
          domainData: domainData ?? null,
          ...getCurrentNextIcon(page, currentSection),
          isToolkit,
        },
      };
    }
  } catch (err) {
    //TODO: add a logger here
  }

  return {
    redirect: {
      permanent: false,
      destination: "/missing",
    },
  };
};

export default RenderComponent;
